import React, { useState, useEffect } from 'react';
import { db, collection, query, where, onSnapshot, updateDoc, doc, serverTimestamp, getDoc, addDoc } from '../firebase';
import { auth } from '../firebase';
import { 
  MessageSquare, Loader2, ArrowLeft, Send, Sparkles, Check, CheckCheck, 
  Reply, X, Smile, MoreVertical, Paperclip, Mic, Camera, FileText, 
  MapPin, UserPlus, Play, Pause, Headphones, Trash2, StopCircle,
  File, Download, ExternalLink, Globe, RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';
import { notify } from '../services/notify';
import { MentionInput } from './ui/MentionInput';
import { GoogleGenAI } from "@google/genai";
import { 
  storage, ref, uploadBytes, getDownloadURL, 
  arrayUnion, increment 
} from '../firebase';
import { v4 as uuidv4 } from 'uuid';
import { motion, AnimatePresence } from 'motion/react';
import { createLocalNotifications } from '../services/notificationService';
import { useExclusiveDropdown } from '../lib/dropdowns';
import { Logger } from '../services/historyService';

const AI_BOT_ID = 'ai-bot-assistant';
const AI_BOT_NAME = 'AI Assistant';
const EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const getLocalAuthToken = () =>
  typeof window === 'undefined' ? null : localStorage.getItem('app_auth_token');

const isLocalSession = () => Boolean(getLocalAuthToken());

const getLocalUser = () => {
  try {
    const payload = getLocalAuthToken()?.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
  } catch {
    return null;
  }
};

const VoiceMessagePlayer = ({ url, duration }: { url: string, duration?: number }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const formatTime = (time: number) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-3 py-1 min-w-[200px]">
      <button 
        onClick={togglePlay}
        className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 transition-all"
      >
        {isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current translate-x-0.5" />}
      </button>
      
      <div className="flex-1 flex flex-col gap-1">
        <div className="h-1 w-full bg-black/10 rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-100" 
            style={{ width: `${progress}%` }} 
          />
        </div>
        <div className="flex justify-between text-[10px] text-[#667781] font-medium">
          <span>{formatTime(currentTime)}</span>
          <span>{duration ? formatTime(duration) : '--:--'}</span>
        </div>
      </div>
      
      <Headphones className="w-4 h-4 text-[#54656f] opacity-40" />
      <audio 
        ref={audioRef} 
        src={url} 
        onTimeUpdate={handleTimeUpdate} 
        onEnded={handleEnded}
        className="hidden"
      />
    </div>
  );
};

const MessageItem = ({ 
  reply, 
  isMe, 
  isAI, 
  language, 
  allReactions, 
  setReplyingTo, 
  setShowReactionsFor, 
  showReactionsFor, 
  handleReaction,
  markAsRead,
  announceDropdownOpen,
  setShowAttachmentMenu,
}: any) => {
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!markAsRead || isMe) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        markAsRead(reply.id);
        observer.disconnect();
      }
    }, { threshold: 0.5 });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [reply.id, isMe]);

  const renderMedia = () => {
    switch (reply.type) {
      case 'image':
        return (
          <div className="mt-1 mb-2 rounded-lg overflow-hidden border border-black/5 max-w-sm">
            <img 
              src={reply.mediaUrl} 
              alt="Sent photo" 
              className="w-full h-auto cursor-pointer hover:scale-[1.02] transition-transform" 
              onClick={() => window.open(reply.mediaUrl, '_blank')}
            />
          </div>
        );
      case 'audio':
        return <VoiceMessagePlayer url={reply.mediaUrl} duration={reply.mediaDuration} />;
      case 'location':
        return (
          <div className="mt-1 mb-2">
            <a 
              href={reply.mediaUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-white/50 rounded-lg border border-black/5 hover:bg-white transition-all group/loc"
            >
              <div className="p-2 bg-red-100 rounded-lg text-red-600 group-hover/loc:scale-110 transition-transform">
                <MapPin className="w-5 h-5" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-bold text-[#111b21]">Live Location</span>
                <span className="text-[10px] text-[#667781] flex items-center gap-1">
                  View on Google Maps <ExternalLink className="w-3 h-3" />
                </span>
              </div>
            </a>
          </div>
        );
      case 'document':
        return (
          <div className="mt-1 mb-2">
            <a 
              href={reply.mediaUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-white/50 rounded-lg border border-black/5 hover:bg-white transition-all group/doc"
            >
              <div className="p-2 bg-blue-100 rounded-lg text-blue-600 group-hover/doc:scale-110 transition-transform">
                <FileText className="w-5 h-5" />
              </div>
              <div className="flex-1 overflow-hidden">
                <div className="text-xs font-bold text-[#111b21] truncate">{reply.mediaName || 'Document'}</div>
                <div className="text-[10px] text-[#667781] flex items-center gap-1 uppercase tracking-widest font-bold">
                  PDF / Document <Download className="w-3 h-3" />
                </div>
              </div>
            </a>
          </div>
        );
      case 'contact':
        return (
          <div className="mt-1 mb-2">
            <div className="flex items-center gap-3 p-3 bg-white/50 rounded-lg border border-black/5">
              <div className="p-2 bg-green-100 rounded-lg text-green-600">
                <UserPlus className="w-5 h-5" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-bold text-[#111b21]">{reply.mediaName || 'Shared Contact'}</span>
                <span className="text-[10px] text-[#667781] uppercase tracking-widest font-bold">vCard Contact</span>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const seenBy = reply.readBy?.filter((r: any) => r.userId !== reply.userId) || [];

  return (
    <div 
      ref={ref}
      className={cn(
        "flex flex-col w-full mb-1 relative",
        isMe ? "items-end" : "items-start"
      )}
    >
      <div className={cn(
        "group relative max-w-[85%] md:max-w-[70%] flex flex-col",
        isMe ? "items-end" : "items-start"
      )}>
        {/* Hover Actions Menu */}
        <div className={cn(
          "absolute top-0 z-20 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-white/90 backdrop-blur-sm rounded-full shadow-md border border-black/5 -translate-y-2",
          isMe ? "right-full mr-2" : "left-full ml-2"
        )}>
          <button 
            onClick={() => setReplyingTo(reply)}
            className="p-1.5 hover:bg-black/5 rounded-full text-[#54656f]"
            title='Reply'
          >
            <Reply className="w-4 h-4" />
          </button>
          <button 
            onClick={() => {
              if (showReactionsFor === reply.id) {
                setShowReactionsFor(null);
                return;
              }
              announceDropdownOpen();
              setShowAttachmentMenu(false);
              setShowReactionsFor(reply.id);
            }}
            className="p-1.5 hover:bg-black/5 rounded-full text-[#54656f]"
            title='React'
          >
            <Smile className="w-4 h-4" />
          </button>
        </div>

        {/* Reaction Picker Popover */}
        <AnimatePresence>
          {showReactionsFor === reply.id && (
            <motion.div 
              initial={{ scale: 0.5, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.5, opacity: 0, y: 10 }}
              className={cn(
                "absolute bottom-full mb-2 z-30 flex items-center bg-white rounded-full shadow-2xl p-1 border border-black/5",
                isMe ? "right-0" : "left-0"
              )}
            >
              {EMOJIS.map(emoji => (
                <button
                  key={emoji}
                  onClick={() => handleReaction(reply.id, emoji)}
                  className="p-2 hover:bg-black/5 hover:scale-125 transition-transform rounded-full text-xl"
                >
                  {emoji}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col">
          {!isMe && (
            <span className={cn(
              "text-[12px] font-bold mb-0.5 ml-2 transition-colors",
              isAI ? "text-primary italic" : "text-[#d62d20]"
            )}>
              {isAI && <Sparkles className="w-3 h-3 inline mr-1" />}
              {reply.userName || 'Team Member'}
            </span>
          )}
          
          <div 
            className={cn(
              "relative px-3 py-2 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] min-w-[120px] transition-all",
              isMe 
                ? "bg-[#dcf8c6] text-[#111b21] rounded-lg rounded-tr-none" 
                : isAI
                  ? "bg-white text-primary rounded-lg rounded-tl-none border border-primary/30"
                  : "bg-[#ffffff] text-[#111b21] rounded-lg rounded-tl-none"
            )}
          >
            {/* Chat bubble tail */}
            <svg 
              viewBox="0 0 8 13" 
              className={cn(
                "absolute top-0 w-2 h-[13px] z-10",
                isMe ? "right-[-7px] fill-[#dcf8c6]" : "left-[-7px] fill-white"
              )}
            >
              {isMe ? (
                <path d="M5.188 1H0v11.193l6.467-8.625C7.526 2.156 6.958 1 5.188 1z" />
              ) : (
                <path d="M2.812 1H8v11.193L1.533 3.568C.474 2.156 1.042 1 2.812 1z" />
              )}
            </svg>

            {/* Quoted Message Preview */}
            {reply.replyTo && (
              <div className="mb-2 p-2 bg-black/5 rounded-md border-l-4 border-primary/40 text-[12px] leading-tight flex items-start gap-2">
                <div className="flex-1 overflow-hidden">
                  <div className="font-bold text-primary opacity-80 mb-0.5 capitalize flex items-center gap-1">
                    <Reply className="w-3 h-3" /> {reply.replyTo.userName}
                  </div>
                  <div className="opacity-70 line-clamp-2 italic flex items-center gap-1">
                    {reply.replyTo.type === 'image' && <Camera className="w-3 h-3" />}
                    {reply.replyTo.type === 'audio' && <Mic className="w-3 h-3" />}
                    {reply.replyTo.type === 'location' && <MapPin className="w-3 h-3" />}
                    {reply.replyTo.type === 'document' && <FileText className="w-3 h-3" />}
                    {reply.replyTo.type && typeof reply.replyTo.type === 'string' && reply.replyTo.type !== 'text' 
                      ? `${reply.replyTo.type.charAt(0).toUpperCase()}${reply.replyTo.type.slice(1)}` 
                      : (reply.replyTo.text || '')}
                  </div>
                </div>
                {reply.replyTo.mediaUrl && reply.replyTo.type === 'image' && (
                  <img src={reply.replyTo.mediaUrl} className="w-10 h-10 object-cover rounded" />
                )}
              </div>
            )}

            {renderMedia()}

            {reply.text && (
              <div className={cn("text-[14px] leading-[1.4] whitespace-pre-wrap font-sans text-[#111b21] mb-1")}>
                {reply.text}
              </div>
            )}

            <div className="flex justify-end items-center gap-1.5 mt-0.5">
              <span className="text-[10px] text-[#667781] font-normal tabular-nums">
                {formatThreadDate(reply.createdAt, language)}
              </span>
              {isMe && (
                <div className="flex items-center">
                  {seenBy.length > 0 ? (
                    <CheckCheck className="w-3.5 h-3.5 text-[#34B7F1]" />
                  ) : (
                    <Check className="w-3.5 h-3.5 text-[#667781]/40" />
                  )}
                </div>
              )}
            </div>

            {/* Reactions display */}
            {Object.keys(allReactions || {}).length > 0 && (
              <div className={cn(
                "flex items-center gap-0.5 absolute top-full -mt-2 bg-white rounded-full px-1.5 py-0.5 shadow-md border border-black/5 transform z-20 hover:scale-105 cursor-default group/reacts",
                isMe ? "right-0" : "left-0"
              )}>
                {Object.entries(allReactions || {}).map(([emoji, count]) => (
                  <div key={emoji} className="flex items-center gap-0.5">
                    <span className="text-[12px]">{emoji}</span>
                    {(count as number) > 1 && <span className="text-[10px] font-extrabold text-[#54656f]">{count as React.ReactNode}</span>}
                  </div>
                ))}
              </div>
            )}
            
            {/* Seen by tooltips or info */}
            {isMe && seenBy.length > 0 && (
              <div className="absolute top-full left-0 mt-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                 <div className="bg-black/80 text-white text-[8px] p-1 rounded whitespace-nowrap">
                   Seen by: {seenBy.map((s: any) => s.userName || 'Unknown').join(', ')}
                 </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
const formatThreadDate = (val: any, lang: string = 'en', showDate = false) => {
  if (!val) return 'Just now';
  try {
    let d: Date;
    if (val && typeof val === 'object' && val.toDate) {
      d = val.toDate();
    } else {
      d = new Date(val);
    }
    
    if (isNaN(d.getTime())) return 'N/A';

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const msgDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    if (!showDate) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    if (msgDate.getTime() === today.getTime()) {
      return lang === 'ar' ? 'اليوم' : 'Today';
    } else if (msgDate.getTime() === yesterday.getTime()) {
      return lang === 'ar' ? 'أمس' : 'Yesterday';
    } else {
      return d.toLocaleDateString();
    }
  } catch (e) {
    return 'N/A';
  }
};

const getSortTime = (val: any) => {
  if (!val) return 0;
  try {
    if (val && typeof val === 'object' && val.toDate) {
      return val.toDate().getTime();
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return 0;
    return d.getTime();
  } catch (e) {
    return 0;
  }
};

export default function DiscussionsView({ orgId, documentId, collectionName, language = 'en', initialThreadId, onClearInitial, onClose }: { orgId: string, documentId?: string, collectionName?: string, language?: string, initialThreadId?: string | null, onClearInitial?: () => void, onClose?: () => void }) {
  const [allThreads, setAllThreads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState<any | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState<any | null>(null);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [showReactionsFor, setShowReactionsFor] = useState<string | null>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const announceDropdownOpen = useExclusiveDropdown('discussions', () => {
    setShowAttachmentMenu(false);
    setShowReactionsFor(null);
  });
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const recordingTimerRef = React.useRef<any>(null);
  const currentUserId = getLocalUser()?.sub || auth.currentUser?.uid;

  const normalizeLocalDiscussion = (discussion: any) => ({
    ...discussion,
    id: discussion.id,
    title: discussion.title || discussion.body || 'Untitled Discussion',
    userId: discussion.createdBy || discussion.created_by || discussion.userId,
    userName: discussion.createdByName || discussion.created_by_name || discussion.userName || 'Anonymous',
    createdAt: discussion.createdAt || discussion.created_at,
    updatedAt: discussion.updatedAt || discussion.updated_at,
    replies: Array.isArray(discussion.messages) ? discussion.messages.map((message: any) => ({
      ...message,
      mediaUrl: message.mediaUrl ?? message.url ?? null,
      mediaName: message.mediaName ?? message.name ?? null,
      mediaDuration: message.mediaDuration ?? message.duration ?? null,
      userName: message.userName ?? message.userEmail ?? 'Team Member',
    })) : [],
    documentId: discussion.documentId || discussion.id,
    documentNumber: discussion.documentNumber || discussion.title || 'N/A',
    documentType: discussion.documentType || 'discussion',
    collectionName: discussion.collectionName || 'discussions'
  });

  const logActivity = async (action: string, targetName: string, metadata: any = {}) => {
    if (!orgId) return;
    await Logger.log(orgId, action, 'Discussions', undefined, targetName, {
      ...metadata,
      userAgent: navigator.userAgent,
      pageName: 'Discussions'
    });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        setAudioChunks(chunks);
        const blob = new Blob(chunks, { type: 'audio/webm' });
        // After stopping, we can show a preview or just send
        // For now, let's stop and then we'll have a separate send button or auto-send
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Error starting recording:", err);
      notify.error("Microphone access denied");
    }
  };

  const stopRecording = (shouldSend: boolean = true) => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.onstop = async () => {
        const chunks = audioChunks.length > 0 ? audioChunks : []; // This might be tricky with async onstop
        // Redefining onstop here to handle the blobs correctly
      };
      
      // Need a more reliable way to get the blob after stop
      const handleStop = async () => {
        const recorder = mediaRecorder;
        const finalChunks: Blob[] = [];
        recorder.ondataavailable = (e) => { if (e.data.size > 0) finalChunks.push(e.data); };
        
        return new Promise<Blob>((resolve) => {
          recorder.onstop = () => {
            const blob = new Blob(finalChunks, { type: 'audio/webm' });
            resolve(blob);
          };
          recorder.stop();
          recorder.stream.getTracks().forEach(track => track.stop());
        });
      };

      handleStop().then(async (blob) => {
        setIsRecording(false);
        clearInterval(recordingTimerRef.current);
        if (shouldSend && blob.size > 0) {
          await uploadMedia(blob, 'audio/webm', 'voice_message.webm', 'audio', recordingDuration);
        }
      });
    }
  };

  const uploadMedia = async (file: Blob | File, contentType: string, fileName: string, type: string, duration?: number) => {
    if (!selectedThread) return;
    const loadingToast = notify.loading(`Uploading ${type}...`);
    try {
      const appAuthToken = localStorage.getItem('app_auth_token');
      let url: string;

      if (appAuthToken) {
        const formData = new FormData();
        formData.append('file', file, fileName);

        const response = await fetch('/api/files/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${appAuthToken}` },
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }

        const uploadResult = await response.json();
        if (!uploadResult?.url) {
          throw new Error('Upload response did not include a URL');
        }
        url = uploadResult.url;
      } else {
        const storageRef = ref(storage, `discussions/${orgId}/${selectedThread.id}/${uuidv4()}_${fileName}`);
        const snapshot = await uploadBytes(storageRef, file, { contentType });
        url = await getDownloadURL(snapshot.ref);
      }
      
      await handleReplyToThread({ type, url, name: fileName, duration });
      await logActivity('MEDIA_UPLOAD', fileName, { type, threadId: selectedThread.id });
      notify.confirm(`${type} sent`, { id: loadingToast });
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      notify.error(`Failed to upload ${type}`, { id: loadingToast });
    }
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraStream(stream);
      setIsCameraOpen(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error opening camera:", err);
      notify.error("Camera access denied");
    }
  };

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    setCameraStream(null);
    setIsCameraOpen(false);
    setPhotoPreview(null);
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg');
        setPhotoPreview(dataUrl);
      }
    }
  };

  const sendPhoto = async () => {
    if (photoPreview) {
      const res = await fetch(photoPreview);
      const blob = await res.blob();
      await uploadMedia(blob, 'image/jpeg', 'camera_photo.jpg', 'image');
      closeCamera();
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadMedia(file, file.type, file.name, type);
    }
    setShowAttachmentMenu(false);
  };

  const sendLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        const locationUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
        await handleReplyToThread({ type: 'location', url: locationUrl, name: 'My Location' });
        await logActivity('LOCATION_SHARE', 'Location', { threadId: selectedThread?.id });
        setShowAttachmentMenu(false);
      }, (error) => {
        notify.error("Could not get your location");
      });
    } else {
      notify.error("Geolocation is not supported by your browser");
    }
  };

  const markAsRead = async (messageId: string) => {
    if (isLocalSession() || !selectedThread || !auth.currentUser?.uid) return;
    try {
      const colName = selectedThread.collectionName;
      const docId = selectedThread.documentId;
      const docRef = doc(db, colName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const docData = docSnap.data();
        let changed = false;
        const updatedThreads = (docData?.threads || []).map((t: any) => {
          if (t.id === selectedThread.id) {
            const updatedReplies = (t.replies || []).map((r: any) => {
              if (r.id === messageId) {
                const readBy = r.readBy || [];
                const alreadyRead = readBy.some((reader: any) => reader.userId === auth.currentUser?.uid);
                if (!alreadyRead) {
                  changed = true;
                  return {
                    ...r,
                    readBy: [...readBy, {
                      userId: auth.currentUser?.uid,
                      userName: auth.currentUser?.displayName || auth.currentUser?.email || 'Anonymous',
                      timestamp: new Date().toISOString()
                    }]
                  };
                }
              }
              return r;
            });
            if (changed) return { ...t, replies: updatedReplies };
          }
          return t;
        });

        if (changed) {
          await updateDoc(docRef, { threads: updatedThreads });
        }
      }
    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    const appAuthToken = localStorage.getItem('app_auth_token');
    if (appAuthToken) {
      let cancelled = false;

      const loadLocalDiscussions = async () => {
        try {
          const response = await fetch(`/api/organizations/${orgId}/discussions`, {
            headers: { Authorization: `Bearer ${appAuthToken}` }
          });

          if (!response.ok) {
            throw new Error(`Failed to load discussions: ${response.status}`);
          }

          const discussions = await response.json();
          if (!cancelled) {
            const normalizedDiscussions = (Array.isArray(discussions) ? discussions : []).map(normalizeLocalDiscussion);
            setAllThreads(currentThreads => {
              const byId = new Map(currentThreads.map(thread => [thread.id, thread]));
              normalizedDiscussions.forEach(discussion => {
                const existing = byId.get(discussion.id);
                const existingTime = getSortTime(existing?.updatedAt || existing?.createdAt);
                const discussionTime = getSortTime(discussion.updatedAt || discussion.createdAt);
                if (!existing || discussionTime >= existingTime) byId.set(discussion.id, discussion);
              });
              return Array.from(byId.values()).sort((a, b) =>
                getSortTime(b.updatedAt || b.createdAt) - getSortTime(a.updatedAt || a.createdAt)
              );
            });
          }
        } catch (error) {
          console.error('Error loading local discussions:', error);
          if (!cancelled) {
            setAllThreads([]);
            notify.error('Failed to load discussions');
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      };

      loadLocalDiscussions();

      // EventSource cannot send Authorization headers, so the already-issued
      // short-lived session token is passed to the authenticated SSE endpoint.
      const stream = new EventSource(
        `/api/organizations/${orgId}/discussions/stream?token=${encodeURIComponent(appAuthToken)}`
      );
      stream.addEventListener('discussion.updated', (event) => {
        try {
          const discussion = normalizeLocalDiscussion(JSON.parse((event as MessageEvent).data));
          if (cancelled) return;
          setAllThreads(currentThreads => {
            const existing = currentThreads.some(thread => thread.id === discussion.id);
            const next = existing
              ? currentThreads.map(thread => thread.id === discussion.id ? discussion : thread)
              : [...currentThreads, discussion];
            return next.sort((a, b) => getSortTime(b.updatedAt || b.createdAt) - getSortTime(a.updatedAt || a.createdAt));
          });
        } catch (error) {
          console.error('Error processing discussion update:', error);
        }
      });
      stream.onerror = () => {
        // EventSource automatically reconnects; avoid showing a transient
        // connection failure as a failed discussion load.
        if (!cancelled) console.warn('Discussion realtime connection interrupted; retrying.');
      };

      return () => {
        cancelled = true;
        stream.close();
      };
    }

    const collectionsToQuery = collectionName ? [{ name: collectionName, type: 'document' }] : [
      { name: 'documents', type: 'document' },
      { name: 'tasks_mops', type: 'task' },
      { name: 'expense_submissions', type: 'expense' }
    ];

    const threadsMap = new Map<string, any[]>();

    const unsubscribes = collectionsToQuery.map(col => {
      const q = query(collection(db, col.name), where('orgId', '==', orgId));
      return onSnapshot(q, (snapshot) => {
        try {
          const threads: any[] = [];
          snapshot.docs.forEach(docSnapshot => {
            if (documentId && docSnapshot.id !== documentId) return;
            const data = docSnapshot.data();
            if (data && data.threads && Array.isArray(data.threads)) {
              data.threads.forEach((thread: any) => {
                threads.push({
                  ...thread,
                  documentId: docSnapshot.id,
                  documentNumber: data.number || data.title || data.vendor || 'N/A',
                  documentType: col.type,
                  collectionName: col.name
                });
              });
            }
          });
          threadsMap.set(col.name, threads);
          
          // Aggregate all threads
          const aggregated: any[] = [];
          threadsMap.forEach(t => aggregated.push(...t));
          // Safe sort
          aggregated.sort((a, b) => getSortTime(b.createdAt) - getSortTime(a.createdAt));
          setAllThreads(aggregated);
          setLoading(false);
        } catch (err) {
          console.error("Error processing threads snapshot:", err);
          setLoading(false);
        }
      }, (error) => {
        console.error("Error in DiscussionsView onSnapshot:", error);
        setLoading(false);
      });
    });

    return () => unsubscribes.forEach(unsub => unsub());
  }, [orgId, documentId, collectionName]);

  useEffect(() => {
    if (initialThreadId && allThreads.length > 0 && !selectedThread) {
      const thread = allThreads.find(t => t.id === initialThreadId || t.documentId === initialThreadId);
      if (thread) {
        setSelectedThread(thread);
        onClearInitial?.();
      }
    }
  }, [allThreads, initialThreadId]);

  useEffect(() => {
    if (!orgId) return;

    const appAuthToken = getLocalAuthToken();
    if (appAuthToken) {
      let cancelled = false;
      void fetch(`/api/organizations/${orgId}/users`, { headers: { Authorization: `Bearer ${appAuthToken}` } })
        .then(async response => {
          if (!response.ok) throw new Error(`Failed to load organization users: ${response.status}`);
          return response.json();
        })
        .then((organizationUsers) => {
          if (cancelled) return;
          const localUsers = (Array.isArray(organizationUsers) ? organizationUsers : []).map((user: any) => ({
            id: user.userId || user.user_id || user.id,
            display: user.displayName || user.display_name || user.email || 'Unnamed User',
          }));
          setUsers([...localUsers, { id: AI_BOT_ID, display: AI_BOT_NAME }]);
        })
        .catch(error => {
          if (!cancelled) console.error('Failed to load local discussion users:', error);
        });
      return () => { cancelled = true; };
    }

    const q = query(collection(db, 'org_users'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allUsers = snapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          display: data.fullName || data.userName || data.email || data.name || 'Unnamed User',
          memberships: data.memberships || [],
          orgId: data.orgId
        };
      });
      const filteredUsers = allUsers.filter(u => 
        u.orgId === orgId || u.memberships?.some((m: any) => m.orgId === orgId)
      );
      
      // Inject AI Bot
      const finalUsers = [
        ...filteredUsers,
        { id: AI_BOT_ID, display: AI_BOT_NAME }
      ];
      
      setUsers(finalUsers);
    });
    return () => unsubscribe();
  }, [orgId]);

  const sendMentionNotifications = async (text: string, threadTitle: string, documentId: string, collectionName: string, threadId: string, discussion?: any) => {
    const localSession = isLocalSession();
    const activeDiscussion = discussion || selectedThread;
    const mentionedUserIds = new Set<string>();
    
    if (users.length === 0 && !localSession) return;

    // Search for @DisplayName patterns using a similar regex to rendering
    // Sort by length descending to catch longer names first
    const sortedUsers = [...users].sort((a, b) => b.display.length - a.display.length);
    const escapedNames = sortedUsers.map(u => u.display.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const mentionRegex = new RegExp(`@(${escapedNames.join('|')})(?![\\w])`, 'g');
    
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      const display = match[1];
      const user = users.find(u => u.display === display);
      if (user && user.id !== auth.currentUser?.uid) {
        mentionedUserIds.add(user.id);
      }
    }

    if (localSession) {
      const participantIds = (activeDiscussion?.replies || [])
        .map((reply: any) => reply.userId)
        .filter(Boolean);
      const recipients = new Set<string>([
        ...mentionedUserIds,
        activeDiscussion?.userId,
        ...participantIds,
      ].filter(Boolean));
      recipients.delete(currentUserId || '');
      await createLocalNotifications(orgId, recipients, {
        title: language === 'ar' ? 'نشاط جديد في المناقشة' : 'New Discussion Activity',
        message: `${auth.currentUser?.displayName || 'Someone'} posted in: ${threadTitle}`,
        type: mentionedUserIds.size > 0 ? 'mention' : 'discussion',
        relatedId: documentId,
        threadId,
      });
      return;
    }

    if (mentionedUserIds.size > 0) {
      const notificationsRef = collection(db, 'notifications');
      const notificationPromises = Array.from(mentionedUserIds).map(userId => {
        return addDoc(notificationsRef, {
          userId: userId,
          orgId,
          title: language === 'ar' ? 'منشن جديد' : 'New Mention',
          message: `${auth.currentUser?.displayName || 'Someone'} mentioned you in: ${threadTitle}`,
          type: 'mention',
          relatedId: documentId,
          collectionName: collectionName,
          threadId: threadId,
          read: false,
          createdAt: serverTimestamp()
        });
      });
      await Promise.all(notificationPromises);
    }

    // Also notify document owner if not the one posting
    try {
      const docRef = doc(db, collectionName, documentId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const docData = docSnap.data();
        if (docData.createdBy && docData.createdBy !== auth.currentUser?.uid && !mentionedUserIds.has(docData.createdBy)) {
          await addDoc(collection(db, 'notifications'), {
            userId: docData.createdBy,
            orgId,
            title: language === 'ar' ? 'نشاط جديد على المستند' : 'New Document Activity',
            message: `${auth.currentUser?.displayName || 'Someone'} posted in a discussion on ${docData.number || 'document'}`,
            type: 'discussion',
            relatedId: documentId,
            collectionName: collectionName,
            read: false,
            createdAt: serverTimestamp()
          });
        }
      }
    } catch (e) {
      console.error("Error sending owner notification:", e);
    }
  };

  useEffect(() => {
    if (selectedThread && allThreads.length > 0) {
      const updated = allThreads.find(t => t.id === selectedThread.id);
      if (updated && JSON.stringify(updated.replies) !== JSON.stringify(selectedThread.replies)) {
        setSelectedThread(updated);
      }
    }
  }, [allThreads]);

  const handleAIResponse = async (text: string, thread: any, colName: string, docId: string, latestReply?: any) => {
    if (isLocalSession()) {
      console.info('AI discussion replies are unavailable in local mode.');
      return;
    }
    try {
      const apiKey = process.env.GEMINI_API_KEY;
      if (!apiKey) {
        console.warn("Gemini API key not found. AI Assistant is disabled.");
        return;
      }

      const ai = new GoogleGenAI({ apiKey });
      
      // Get context from ALL threads for this document to have full module access
      const relatedThreads = allThreads.filter(t => t.documentId === docId);
      let discussionContext = "";
      
      relatedThreads.forEach(t => {
        discussionContext += `\n[Thread Title: ${t.title}]\n`;
        const replies = t.replies || [];
        // Include latest reply if it belongs to this thread and isn't saved in state yet
        const effectiveReplies = (t.id === thread.id && latestReply) ? [...replies, latestReply] : replies;
        
        effectiveReplies.forEach((r: any) => {
          discussionContext += `${r.userName}: ${r.text}\n`;
        });
      });
      
      const prompt = `
        You are a high-level Financial Controller and AI Assistant (CFA/CMA) within a corporate ERP system.
        The user mentioned you in the discussion module for document reference #${thread.documentNumber || docId}.
        
        CORE CONTEXT (All discussions for this document):
        ${discussionContext}
        
        USER COMMAND IN CURRENT THREAD "${thread.title}":
        "${text}"
        
        INSTRUCTIONS:
        1. PERSISTENCE: You have full access to this discussion module. Acknowledge that you have analyzed the entire conversation history above.
        2. SUMMARY/QUERY: If the user asks for a summary, provide a professional, one-second "flash summary" followed by bullet points of key decisions, financial impacts, or concerns.
        3. MEMORY: Confirm you are tracking this document's lifecycle. If asked about it in the future, you will refer to these specific details.
        4. TONE: Lead Management Accountant. Precise, professional, and audit-ready.
        5. OUTPUT: Your response will be posted directly to the thread as a reply.
        
        Assistant Response:
      `;

      const result = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: prompt
      });

      const aiReplyText = result.text;
      if (!aiReplyText) return;

      const aiReply = {
        id: 'ai-' + Math.random().toString(36).substr(2, 9),
        text: aiReplyText,
        userId: AI_BOT_ID,
        userName: AI_BOT_NAME,
        createdAt: new Date().toISOString()
      };

      // Add AI reply to Firestore
      const docRef = doc(db, colName, docId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const docData = docSnap.data();
        const updatedThreads = (docData?.threads || []).map((t: any) => 
          t.id === thread.id ? { 
            ...t, 
            replies: [...(t.replies || []), aiReply],
            updatedAt: new Date().toISOString() 
          } : t
        );

        await updateDoc(docRef, { 
          threads: updatedThreads,
          updatedAt: serverTimestamp()
        });
      }

    } catch (err) {
      console.error("AI Assistant error:", err);
    }
  };

  const handleReaction = async (messageId: string, emoji: string) => {
    if (isLocalSession()) {
      setShowReactionsFor(null);
      notify.confirm('Message reactions are not yet available in local mode.');
      return;
    }
    if (!selectedThread) return;
    try {
      const colName = selectedThread.collectionName;
      const docId = selectedThread.documentId;
      const docRef = doc(db, colName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const docData = docSnap.data();
        const updatedThreads = (docData?.threads || []).map((t: any) => {
          if (t.id === selectedThread.id) {
            const updatedReplies = (t.replies || []).map((r: any) => {
              if (r.id === messageId) {
                const currentReactions = r.reactions || {};
                const userReactions = currentReactions[auth.currentUser?.uid || ''] || [];
                
                let newUserReactions;
                if (userReactions.includes(emoji)) {
                  newUserReactions = userReactions.filter((e: string) => e !== emoji);
                } else {
                  newUserReactions = [emoji]; // Allow only one reaction per user for simplicity or add more? WhatsApp allows one.
                }

                return {
                  ...r,
                  reactions: {
                    ...currentReactions,
                    [auth.currentUser?.uid || '']: newUserReactions
                  }
                };
              }
              return r;
            });
            return { ...t, replies: updatedReplies };
          }
          return t;
        });

        await updateDoc(docRef, { threads: updatedThreads });
        setShowReactionsFor(null);
      }
    } catch (error) {
      console.error("Error adding reaction:", error);
      notify.error("Failed to add reaction");
    }
  };

  const handleReplyToThread = async (mediaData?: { type: string, url: string, name?: string, duration?: number }) => {
    if (!replyText.trim() && !mediaData && !replyingTo) return;

    const newReply = {
      id: uuidv4(),
      text: replyText,
      userId: auth.currentUser?.uid,
      userName: auth.currentUser?.displayName || auth.currentUser?.email || 'Anonymous',
      createdAt: new Date().toISOString(),
      type: mediaData?.type || 'text',
      mediaUrl: mediaData?.url || null,
      mediaName: mediaData?.name || null,
      mediaDuration: mediaData?.duration || null,
      readBy: [{
        userId: auth.currentUser?.uid,
        userName: auth.currentUser?.displayName || auth.currentUser?.email || 'Anonymous',
        timestamp: new Date().toISOString()
      }],
      replyTo: replyingTo ? {
        id: replyingTo.id,
        text: replyingTo.text,
        userName: replyingTo.userName,
        type: replyingTo.type || 'text',
        mediaUrl: replyingTo.mediaUrl || null
      } : null
    };

    try {
      const appAuthToken = localStorage.getItem('app_auth_token');
      if (appAuthToken) {
        const response = await fetch(`/api/organizations/${orgId}/discussions/${selectedThread.id}/replies`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${appAuthToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text: replyText,
            type: mediaData?.type || 'text',
            mediaUrl: mediaData?.url || null,
            mediaName: mediaData?.name || null,
            mediaDuration: mediaData?.duration || null,
            replyTo: newReply.replyTo
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to post reply: ${response.status}`);
        }

        const result = await response.json();
        const discussion = result.discussion || result;
        let updatedThread = normalizeLocalDiscussion(discussion);
        const message = result.message || result.reply;
        if (message && !updatedThread.replies.some((reply: any) => reply.id === message.id)) {
          updatedThread = {
            ...updatedThread,
            replies: [...updatedThread.replies, message]
          };
        }

        await sendMentionNotifications(
          replyText,
          updatedThread.title,
          updatedThread.id,
          'discussions',
          updatedThread.id,
          updatedThread,
        );
        setSelectedThread(updatedThread);
        setAllThreads(currentThreads => currentThreads
          .map(thread => thread.id === updatedThread.id ? updatedThread : thread)
          .sort((a, b) => getSortTime(b.createdAt) - getSortTime(a.createdAt)));
        setReplyText('');
        setReplyingTo(null);
        return;
      }

      const colName = selectedThread.collectionName;
      const docId = selectedThread.documentId;
      
      if (!colName || !docId) {
        throw new Error("Missing document context for this thread");
      }

      const docRef = doc(db, colName, docId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error("Target document no longer exists");
      }
      
      const docData = docSnap.data();
      const updatedThreads = (docData?.threads || []).map((t: any) => 
        t.id === selectedThread.id ? { 
          ...t, 
          replies: [...(t.replies || []), newReply],
          updatedAt: new Date().toISOString() 
        } : t
      );

      await updateDoc(docRef, { 
        threads: updatedThreads,
        updatedAt: serverTimestamp()
      });

      // Check for AI Mention
      if (replyText.includes(`@${AI_BOT_NAME}`)) {
        handleAIResponse(replyText, selectedThread, colName, docId, newReply);
      }

      await sendMentionNotifications(
        replyText, 
        selectedThread.title, 
        docId, 
        colName, 
        selectedThread.id
      );

      setReplyText('');
      setReplyingTo(null);
    } catch (error: any) {
      console.error("Error replying to thread:", error);
      const isPermissionError = error.message?.includes('permissions') || error.code === 'permission-denied';
      notify.error(isPermissionError ? "Permission denied. You might not have access to this document." : "Failed to post reply");
    }
  };

  const handleCreateThread = async (e?: React.FormEvent) => {
    if (e && e.preventDefault) e.preventDefault();
    const appAuthToken = localStorage.getItem('app_auth_token');
    if (!newThreadTitle.trim() || (!appAuthToken && (!documentId || !collectionName))) return;

    if (appAuthToken) {
      try {
        const response = await fetch(`/api/organizations/${orgId}/discussions`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${appAuthToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ title: newThreadTitle.trim() })
        });

        if (!response.ok) {
          throw new Error(`Failed to create discussion: ${response.status}`);
        }

        const discussion = normalizeLocalDiscussion(await response.json());
        await sendMentionNotifications(
          newThreadTitle,
          discussion.title,
          discussion.id,
          'discussions',
          discussion.id,
          discussion,
        );
        setAllThreads(currentThreads => [discussion, ...currentThreads]
          .sort((a, b) => getSortTime(b.createdAt) - getSortTime(a.createdAt)));
        setNewThreadTitle('');
        setIsCreatingThread(false);
      } catch (error) {
        console.error('Error creating local discussion:', error);
        notify.error('Failed to create discussion');
      }
      return;
    }

    const threadId = Math.random().toString(36).substr(2, 9);
    const newThread = {
      id: threadId,
      title: newThreadTitle,
      userId: auth.currentUser?.uid,
      userName: auth.currentUser?.displayName || auth.currentUser?.email || 'Anonymous',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      replies: []
    };

    try {
      const docRef = doc(db, collectionName, documentId);
      const docSnap = await getDoc(docRef);
      const docData = docSnap.data();
      
      await updateDoc(docRef, { 
        threads: [...(docData?.threads || []), newThread],
        updatedAt: serverTimestamp()
      });
      
      await sendMentionNotifications(
        newThreadTitle, 
        newThreadTitle, 
        documentId, 
        collectionName, 
        threadId
      );

      setNewThreadTitle('');
      setIsCreatingThread(false);
    } catch (error: any) {
      console.error("Error creating thread:", error);
      const isPermissionError = error.message?.includes('permissions') || error.code === 'permission-denied';
      notify.error(isPermissionError ? "Permission denied. You might not have access to this document." : "Failed to create thread");
    }
  };

  const renderTextWithMentions = (text: any) => {
    try {
      if (text === null || text === undefined) return null;
      const str = String(text);
      if (!str.trim()) return null;
      
      // If we have no users, just return string
      if (users.length === 0) return str;

      // Sort users by display name length descending to avoid matching "John" when "John Doe" is mentioned
      const sortedUsers = [...users].sort((a, b) => b.display.length - a.display.length);
      
      // Create a regex that matches any of the user display names
      // Escape special characters in display names
      const escapedNames = sortedUsers.map(u => {
        const escaped = u.display.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return `@${escaped}`;
      });
      
      const mentionRegex = new RegExp(`(${escapedNames.join('|')})(?![\\w])`, 'g');
      const parts = str.split(mentionRegex);
      
      if (parts.length === 1) return str;
      
      return parts.map((part: string, i: number) => {
        const matchingUser = sortedUsers.find(u => `@${u.display}` === part);
        if (matchingUser) {
          return (
            <span key={`${i}-${matchingUser.id}`} className="text-indigo-700 font-black bg-indigo-100/80 px-1.5 py-0.5 rounded cursor-pointer hover:bg-indigo-600 hover:text-white transition-colors">
              {part}
            </span>
          );
        }
        return part;
      });
    } catch (e) {
      console.error("Error rendering mentions:", e);
      return String(text || '');
    }
  };

  const scrollRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedThread && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [selectedThread?.replies]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 h-64 space-y-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm opacity-50 font-bold uppercase tracking-widest">
          {language === 'ar' ? 'جاري التحميل...' : 'Loading discussions...'}
        </p>
      </div>
    );
  }

  if (selectedThread) {
    return (
      <div className="flex flex-col h-full animate-in fade-in slide-in-from-bottom-4 duration-300 bg-[#e5ddd5] relative">
        {/* Sticky Header */}
        <div className="sticky top-0 z-30 bg-[#f0f2f5] px-4 py-3 flex items-center justify-between shadow-sm border-b border-black/5">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSelectedThread(null)} 
              className="p-2 hover:bg-black/5 rounded-full transition-all"
            >
              <ArrowLeft className="w-5 h-5 text-[#54656f]" /> 
            </button>
            <div className="flex flex-col">
              <h2 className="text-base font-bold text-[#111b21] leading-tight flex items-center gap-2">
                {selectedThread.title || (language === 'ar' ? 'بدون عنوان' : 'Untitled Discussion')}
                {selectedThread.documentNumber && (
                  <span className="text-[10px] bg-black/10 px-1.5 py-0.5 rounded text-[#54656f] font-mono">
                    #{selectedThread.documentNumber}
                  </span>
                )}
              </h2>
              <div className="text-[11px] text-[#667781] font-medium">
                By {selectedThread.userName || 'Anonymous'} • {formatThreadDate(selectedThread.createdAt, language)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-[#54656f]">
            <MoreVertical className="w-5 h-5 cursor-pointer opacity-70 hover:opacity-100" />
          </div>
        </div>
        
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 md:px-8 py-6 custom-scrollbar relative"
          style={{ 
            backgroundImage: 'url("https://w0.peakpx.com/wallpaper/580/678/OH-wallpaper-whatsapp-doodle-patterns-white-monochrome.jpg")',
            backgroundSize: '400px',
            backgroundRepeat: 'repeat',
            backgroundBlendMode: 'overlay',
            backgroundColor: '#e5ddd5'
          }}
        >
          <div className="flex flex-col gap-2 max-w-5xl mx-auto min-h-full pb-10">                
            {(selectedThread.replies && Array.isArray(selectedThread.replies) && selectedThread.replies.length > 0) ? (
              selectedThread.replies.map((reply: any, index: number) => {
                if (!reply) return null;
                const isMe = reply.userId === currentUserId;
                const isAI = reply.userId === AI_BOT_ID;

                // Sticky date logic
                const prevReply = selectedThread.replies[index - 1];
                const currentDate = formatThreadDate(reply.createdAt, language, true);
                const prevDate = prevReply ? formatThreadDate(prevReply.createdAt, language, true) : null;
                const showStickyDate = currentDate !== prevDate;

                // Reactions aggregation
                const allReactions: { [emoji: string]: number } = {};
                if (reply.reactions) {
                  Object.values(reply.reactions).forEach((userReacs: any) => {
                    if (Array.isArray(userReacs)) {
                      userReacs.forEach((emoji: string) => {
                        allReactions[emoji] = (allReactions[emoji] || 0) + 1;
                      });
                    }
                  });
                }

                return (
                  <React.Fragment key={reply.id || `reply-${index}`}>
                    {showStickyDate && (
                      <div className="flex justify-center my-4 sticky top-16 z-20">
                        <span className="px-3 py-1.5 bg-[#ffffff]/90 backdrop-blur-sm text-[11px] font-medium text-[#54656f] rounded-lg shadow-sm uppercase tracking-wider">
                          {currentDate}
                        </span>
                      </div>
                    )}
                    <MessageItem 
                      reply={reply}
                      isMe={isMe}
                      isAI={isAI}
                      language={language}
                      allReactions={allReactions}
                      setReplyingTo={setReplyingTo}
                      setShowReactionsFor={setShowReactionsFor}
                      showReactionsFor={showReactionsFor}
                      handleReaction={handleReaction}
                      markAsRead={markAsRead}
                      announceDropdownOpen={announceDropdownOpen}
                      setShowAttachmentMenu={setShowAttachmentMenu}
                    />
                  </React.Fragment>
                );
              })
            ) : (
              <div className="p-12 text-center border-2 border-dashed border-black/10 rounded-[32px] bg-white/50 backdrop-blur-sm">
                <p className="text-sm text-primary opacity-40 font-bold italic serif">
                  {language === 'ar' ? 'لا يوجد ردود بعد. كن أول من يشارك!' : 'No replies yet. Be the first to join the conversation!'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sticky Input Bar */}
        <div className="sticky bottom-0 z-30 bg-[#f0f2f5] px-4 py-3 flex flex-col gap-2 shadow-[0_-1px_3px_rgba(0,0,0,0.05)]">
          <AnimatePresence>
            {isCameraOpen && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center p-4"
              >
                <div className="relative w-full max-w-2xl aspect-video bg-zinc-900 rounded-3xl overflow-hidden shadow-2xl">
                  {photoPreview ? (
                    <img src={photoPreview} className="w-full h-full object-cover" />
                  ) : (
                    <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                  )}
                  
                  <button onClick={closeCamera} className="absolute top-6 right-6 p-3 bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="mt-8 flex items-center gap-8">
                  {photoPreview ? (
                    <>
                      <button onClick={() => setPhotoPreview(null)} className="flex flex-col items-center gap-2 text-white/60 hover:text-white transition-colors">
                        <div className="p-4 bg-white/10 rounded-full"><RefreshCw className="w-8 h-8" /></div>
                        <span className="text-xs font-bold uppercase tracking-widest">Retake</span>
                      </button>
                      <button onClick={sendPhoto} className="flex flex-col items-center gap-2 text-white">
                        <div className="p-6 bg-[#00a884] rounded-full shadow-lg hover:scale-110 active:scale-95 transition-all"><Send className="w-10 h-10" /></div>
                        <span className="text-sm font-bold uppercase tracking-widest">Send Photo</span>
                      </button>
                    </>
                  ) : (
                    <button onClick={capturePhoto} className="p-1 border-4 border-white rounded-full">
                      <div className="w-16 h-16 bg-white rounded-full hover:scale-95 transition-transform" />
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {replyingTo && (
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 border-l-4 border-primary/60 group animate-in slide-in-from-bottom-2 duration-200 shadow-sm max-w-5xl mx-auto w-full">
               <div className="flex-1 overflow-hidden">
                 <div className="text-[11px] font-bold text-primary mb-0.5 flex items-center gap-1">
                   <Reply className="w-3 h-3" /> {replyingTo.userName}
                 </div>
                 <div className="text-xs text-[#667781] line-clamp-1 italic">
                    {replyingTo.type === 'image' && <Camera className="w-3 h-3 inline mr-1" />}
                    {replyingTo.type === 'audio' && <Mic className="w-3 h-3 inline mr-1" />}
                    {replyingTo.type !== 'text' ? replyingTo.type : replyingTo.text}
                 </div>
               </div>
               {replyingTo.mediaUrl && replyingTo.type === 'image' && (
                 <img src={replyingTo.mediaUrl} className="w-10 h-10 object-cover rounded" />
               )}
               <button onClick={() => setReplyingTo(null)} className="p-1 hover:bg-black/5 rounded-full transition-colors">
                 <X className="w-4 h-4 text-[#54656f]" />
               </button>
            </div>
          )}
          
          <div className="flex gap-2 items-center max-w-5xl mx-auto w-full relative">
            <div className="flex items-center gap-1">
              <div className="relative">
                <button 
                  onClick={() => {
                    if (showAttachmentMenu) {
                      setShowAttachmentMenu(false);
                      return;
                    }
                    announceDropdownOpen();
                    setShowReactionsFor(null);
                    setShowAttachmentMenu(true);
                  }}
                  className={cn(
                    "p-2.5 rounded-full transition-all",
                    showAttachmentMenu ? "bg-primary text-white rotate-45" : "text-[#54656f] hover:bg-black/5"
                  )}
                >
                  <Paperclip className="w-6 h-6" />
                </button>

                <AnimatePresence>
                  {showAttachmentMenu && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.9 }}
                      className="absolute bottom-full left-0 mb-4 bg-white rounded-3xl shadow-2xl p-4 grid grid-cols-2 gap-4 border border-black/5 min-w-[240px] z-40"
                    >
                      <label className="flex flex-col items-center gap-2 p-3 hover:bg-black/5 rounded-2xl cursor-pointer transition-colors group">
                        <div className="p-3 bg-blue-100 text-blue-600 rounded-full group-hover:scale-110 transition-transform"><FileText className="w-6 h-6" /></div>
                        <span className="text-[10px] font-bold uppercase text-[#54656f]">Document</span>
                        <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, 'document')} />
                      </label>
                      <button onClick={openCamera} className="flex flex-col items-center gap-2 p-3 hover:bg-black/5 rounded-2xl transition-colors group">
                        <div className="p-3 bg-pink-100 text-pink-600 rounded-full group-hover:scale-110 transition-transform"><Camera className="w-6 h-6" /></div>
                        <span className="text-[10px] font-bold uppercase text-[#54656f]">Camera</span>
                      </button>
                      <label className="flex flex-col items-center gap-2 p-3 hover:bg-black/5 rounded-2xl cursor-pointer transition-colors group">
                        <div className="p-3 bg-purple-100 text-purple-600 rounded-full group-hover:scale-110 transition-transform"><Camera className="w-6 h-6" /></div>
                        <span className="text-[10px] font-bold uppercase text-[#54656f]">Gallery</span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'image')} />
                      </label>
                      <button onClick={sendLocation} className="flex flex-col items-center gap-2 p-3 hover:bg-black/5 rounded-2xl transition-colors group">
                        <div className="p-3 bg-green-100 text-green-600 rounded-full group-hover:scale-110 transition-transform"><MapPin className="w-6 h-6" /></div>
                        <span className="text-[10px] font-bold uppercase text-[#54656f]">Location</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="flex-1 min-h-[44px] relative">
              {isRecording ? (
                <div className="flex items-center gap-3 px-5 py-2.5 bg-white rounded-[22px] shadow-sm animate-pulse">
                  <div className="w-2 h-2 bg-red-500 rounded-full" />
                  <span className="text-[15px] font-medium text-red-500 flex-1">
                    Recording... {Math.floor(recordingDuration / 60)}:{ (recordingDuration % 60).toString().padStart(2, '0') }
                  </span>
                  <button onClick={() => stopRecording(false)} className="text-[#54656f] hover:text-red-500 transition-colors">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <MentionInput
                  value={replyText}
                  onChange={setReplyText}
                  users={users}
                  onKeyDown={(e: any) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleReplyToThread();
                    }
                  }}
                  placeholder={language === 'ar' ? 'اكتب رسالة...' : 'Type a message...'}
                  className="w-full rounded-[22px] border-none min-h-[44px] px-5 py-2.5 focus:outline-none bg-white shadow-sm text-[#111b21] text-[15px]"
                />
              )}
            </div>
            
            {!replyText.trim() && !isRecording ? (
              <button 
                onClick={startRecording}
                className="p-3 text-[#54656f] hover:bg-black/5 rounded-full transition-all"
              >
                <Mic className="w-6 h-6" />
              </button>
            ) : isRecording ? (
              <button 
                onClick={() => stopRecording(true)}
                className="p-3 bg-[#00a884] text-white rounded-full h-[48px] w-[48px] flex items-center justify-center shrink-0 shadow-md animate-in zoom-in duration-200"
              >
                <StopCircle className="w-6 h-6" />
              </button>
            ) : (
              <button 
                onClick={() => handleReplyToThread()} 
                disabled={!replyText.trim()}
                className="p-3 bg-[#00a884] text-white rounded-full h-[48px] w-[48px] flex items-center justify-center shrink-0 hover:brightness-95 active:scale-95 transition-all shadow-md disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-6 animate-in fade-in duration-500 overflow-hidden">
      <div className="flex-none flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-primary italic serif">
            {documentId ? (language === 'ar' ? 'مناقشات المستند' : 'Document Discussions') : (language === 'ar' ? 'جميع المناقشات' : 'All Discussions')}
          </h2>
          <p className="text-[10px] font-bold uppercase tracking-widest text-primary opacity-40 mt-1">
            {allThreads.length} {language === 'ar' ? 'موضوع' : 'Threads Available'}
          </p>
        </div>
        {(documentId || localStorage.getItem('app_auth_token')) && !isCreatingThread && (
          <button 
            onClick={() => setIsCreatingThread(true)} 
            className="px-6 py-3 bg-primary text-primary-foreground rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:opacity-90 transition-all shadow-lg"
          >
            {language === 'ar' ? 'موضوع جديد' : 'New Thread'}
          </button>
        )}
      </div>

      {isCreatingThread && (
        <div className="flex gap-2 items-end bg-primary/5 p-6 rounded-[32px] border border-primary/20 animate-in slide-in-from-top-4 duration-300">
          <div className="flex-1 min-h-[52px]">
            <MentionInput
              value={newThreadTitle}
              onChange={setNewThreadTitle}
              users={users}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleCreateThread();
                }
              }}
              placeholder={language === 'ar' ? 'عنوان الموضوع...' : 'Thread title...'}
              className="w-full rounded-2xl border border-primary/20 h-[52px] p-3 focus:outline-none focus:border-primary shadow-sm resize-none bg-white text-primary"
            />
          </div>
          <button 
            onClick={() => handleCreateThread()} 
            disabled={!newThreadTitle.trim()}
            className="p-3 bg-primary text-primary-foreground rounded-xl h-[52px] w-[52px] flex items-center justify-center shrink-0 hover:opacity-90 disabled:opacity-20 transition-all shadow-md"
          >
            <Send className="w-5 h-5" />
          </button>
          <button 
            onClick={() => setIsCreatingThread(false)} 
            className="px-5 bg-white border border-primary/20 text-primary rounded-xl h-[52px] flex items-center justify-center text-[10px] font-bold uppercase tracking-widest hover:bg-primary/5 transition-all"
          >
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 space-y-4 overflow-y-auto pr-2 custom-scrollbar">
        {allThreads.length === 0 ? (
          <div className="p-16 text-center border-2 border-dashed border-primary/10 rounded-[40px] bg-primary/5">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-10 text-primary" />
            <p className="text-sm font-bold uppercase tracking-widest text-primary opacity-30 italic serif">
              {language === 'ar' ? 'لا يوجد مناقشات بعد' : 'No discussions found yet.'}
            </p>
            {(documentId || localStorage.getItem('app_auth_token')) && (
              <button 
                onClick={() => setIsCreatingThread(true)}
                className="mt-6 text-xs text-primary font-bold underline underline-offset-4 uppercase tracking-wider hover:opacity-70 transition-all italic serif"
              >
                {language === 'ar' ? 'ابدأ محادثة' : 'Start a conversation'}
              </button>
            )}
          </div>
        ) : (
          allThreads.map((thread, index) => (
            <div 
              key={thread.id || `thread-${index}`} 
              onClick={() => setSelectedThread(thread)}
              className="group bg-white p-6 rounded-[32px] border border-primary/10 shadow-sm cursor-pointer hover:border-primary hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="space-y-1">
                  <h3 className="font-bold text-lg group-hover:text-primary transition-colors leading-tight italic serif text-slate-800">
                    {renderTextWithMentions(thread.title) || 'Untitled'}
                  </h3>
                  {!documentId && (
                    <div className="flex items-center gap-2">
                       <span className="px-2 py-0.5 bg-primary text-primary-foreground rounded text-[8px] font-black uppercase tracking-[0.2em]">
                        {thread.documentType || 'Doc'}
                      </span>
                      <span className="text-[10px] font-bold text-primary">
                        {thread.documentNumber}
                      </span>
                    </div>
                  )}
                </div>
                <div className="bg-primary/10 px-3 py-1.5 rounded-2xl">
                  <span className="text-[10px] font-bold text-primary">
                    {thread.replies?.length || 0}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-primary/10">
                <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-primary opacity-40">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
                    {(thread.userName || 'A').charAt(0).toUpperCase()}
                  </div>
                  <span>{thread.userName || 'Anonymous'}</span>
                </div>
                <span className="text-[9px] font-bold text-primary/30 uppercase tracking-tighter italic serif">
                  {formatThreadDate(thread.updatedAt || thread.createdAt, language)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
